import type {
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockGroupsSaveTimeClockGroupRequest,
  FacilityFlowCoreModulesPlanningWorkflowsTimeClockGroupsTimeClockGroupDto,
} from '@/api/generated/models';
import { isValidStampingRule } from '../TenantSettings/timeClockPolicyForm';

/** Limits mirror the backend validation behind `planning.timeClock.invalidGroup`. */
export const GROUP_LIMITS = {
  maxNameLength: 100,
  maxPeriods: 12,
} as const;

export interface PeriodForm {
  /** Stable React key — not sent to the server. */
  key: number;
  fromMonth: number;
  toMonth: number;
  stampingStartHour: number;
  stampingEndHour: number;
  workCountStartHour: number;
}

export interface GroupForm {
  name: string;
  isActive: boolean;
  periods: PeriodForm[];
}

export interface GroupErrors {
  name?: string;
  periods?: string;
  /** Per period key: window rule broken. */
  periodWindow?: Record<number, string>;
  /** Per period key: months overlap with an earlier period. */
  periodOverlap?: Record<number, string>;
}

let nextPeriodKey = 1;

export function newPeriod(patch: Partial<Omit<PeriodForm, 'key'>> = {}): PeriodForm {
  return {
    key: nextPeriodKey++,
    fromMonth: 1,
    toMonth: 12,
    stampingStartHour: 5,
    stampingEndHour: 20,
    workCountStartHour: 6,
    ...patch,
  };
}

export function emptyGroupForm(): GroupForm {
  return { name: '', isActive: true, periods: [newPeriod()] };
}

export function groupToForm(
  group: FacilityFlowCoreModulesPlanningWorkflowsTimeClockGroupsTimeClockGroupDto,
): GroupForm {
  return {
    name: group.name ?? '',
    isActive: group.isActive ?? true,
    periods: (group.periods ?? []).map((p) =>
      newPeriod({
        fromMonth: p.fromMonth ?? 1,
        toMonth: p.toMonth ?? 12,
        stampingStartHour: p.stampingStartHour ?? 0,
        stampingEndHour: p.stampingEndHour ?? 24,
        workCountStartHour: p.workCountStartHour ?? 0,
      }),
    ),
  };
}

export function formToRequest(
  form: GroupForm,
): FacilityFlowCoreModulesPlanningWorkflowsTimeClockGroupsSaveTimeClockGroupRequest {
  return {
    name: form.name.trim(),
    isActive: form.isActive,
    periods: form.periods.map((p) => ({
      fromMonth: p.fromMonth,
      toMonth: p.toMonth,
      stampingStartHour: p.stampingStartHour,
      stampingEndHour: p.stampingEndHour,
      workCountStartHour: p.workCountStartHour,
    })),
  };
}

export function groupFormsEqual(a: GroupForm, b: GroupForm): boolean {
  return JSON.stringify(formToRequest(a)) === JSON.stringify(formToRequest(b));
}

/**
 * Months (1–12) a period covers, `fromMonth` to `toMonth` inclusive. A range may wrap over the
 * turn of the year, e.g. 11 → 3 covers Nov, Dec, Jan, Feb, Mar.
 */
export function monthsOf(fromMonth: number, toMonth: number): number[] {
  const months: number[] = [];
  let month = fromMonth;
  for (let i = 0; i < 12; i++) {
    months.push(month);
    if (month === toMonth) break;
    month = (month % 12) + 1;
  }
  return months;
}

/**
 * Keys of the periods whose months overlap a period listed before them — the same check the
 * backend runs (`planning.timeClock.invalidGroup`).
 */
export function overlappingPeriodKeys(
  periods: Pick<PeriodForm, 'key' | 'fromMonth' | 'toMonth'>[],
): Set<number> {
  const covered = new Set<number>();
  const overlapping = new Set<number>();
  for (const period of periods) {
    for (const month of monthsOf(period.fromMonth, period.toMonth)) {
      if (covered.has(month)) overlapping.add(period.key);
      covered.add(month);
    }
  }
  return overlapping;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Client-side mirror of the backend rules. `t` resolves keys of the `timeClockGroups` namespace. */
export function validateGroupForm(form: GroupForm, t: Translate): GroupErrors {
  const errors: GroupErrors = {};
  const name = form.name.trim();
  if (name === '') {
    errors.name = t('validation.nameRequired');
  } else if (name.length > GROUP_LIMITS.maxNameLength) {
    errors.name = t('validation.nameTooLong', { max: GROUP_LIMITS.maxNameLength });
  }

  if (form.periods.length === 0) {
    errors.periods = t('validation.periodsRequired');
  } else if (form.periods.length > GROUP_LIMITS.maxPeriods) {
    errors.periods = t('validation.tooManyPeriods', { max: GROUP_LIMITS.maxPeriods });
  }

  const periodWindow: Record<number, string> = {};
  for (const period of form.periods) {
    if (!isValidStampingRule(period.stampingStartHour, period.stampingEndHour, period.workCountStartHour)) {
      periodWindow[period.key] = t('validation.windowRule');
    }
  }
  if (Object.keys(periodWindow).length > 0) errors.periodWindow = periodWindow;

  const periodOverlap: Record<number, string> = {};
  for (const key of overlappingPeriodKeys(form.periods)) {
    periodOverlap[key] = t('validation.periodOverlap');
  }
  if (Object.keys(periodOverlap).length > 0) errors.periodOverlap = periodOverlap;

  return errors;
}

export function hasGroupErrors(errors: GroupErrors): boolean {
  return Object.keys(errors).length > 0;
}
